import { apiEndpoints } from '@/lib/api/endpoints'
import { resolveAnonymousTrackingId } from '@/lib/analytics/datadogUser'
import { ATTRIBUTION_PARAMS } from '@/hooks/useAttributionTracking'
import { appPath } from '@/lib/getPublicAppUrl'

/**
 * Payload for a CampaignTouch — a single UTM-bearing landing recorded for
 * multi-touch attribution. Keyed by the Segment anonymous id and free of any
 * PII: no phone, email, name, or patient id ever belongs here.
 *
 * Field names are snake_case to match the Nile-Core request contract at the
 * network boundary.
 */
export interface CampaignTouchPayload {
    anonymous_id: string
    utm_source: string
    utm_medium?: string
    utm_campaign?: string
    utm_content?: string
    gclid?: string
    gbraid?: string
    wbraid?: string
    fbclid?: string
    msclkid?: string
    kad_cid?: string
    oppref?: string
    fbc?: string
    fbp?: string
    in_app_browser?: string
    landing_path?: string
}

interface BuildCampaignTouchPayloadInput {
    searchParams: URLSearchParams
    anonymousId: string | null | undefined
    landingPath: string
    inAppBrowser?: string
    fbc?: string
    fbp?: string
}

/**
 * Builds a stable fingerprint of the UTM + click-id set in a URL. Two landings
 * with the same attribution parameters produce the same string, letting the
 * per-tab session guard skip duplicate touches without suppressing genuinely
 * new campaigns.
 */
export function computeCampaignTouchFingerprint(searchParams: URLSearchParams): string {
    return ATTRIBUTION_PARAMS.map((param) => searchParams.get(param) ?? '').join('|')
}

/**
 * Constructs the CampaignTouch request body from the URL and resolved
 * anonymous id, or returns null when the two required fields (utm_source and a
 * Segment anonymous id) are not both present.
 *
 * The payload is assembled from an explicit allow-list of attribution fields
 * so no arbitrary query params — and no PII or patient id — can leak into it.
 */
export function buildCampaignTouchPayload(input: BuildCampaignTouchPayloadInput): CampaignTouchPayload | null {
    const { searchParams, anonymousId, landingPath, inAppBrowser, fbc, fbp } = input

    const utmSource = searchParams.get('utm_source')
    if (!utmSource || !anonymousId) {
        return null
    }

    const payload: CampaignTouchPayload = {
        anonymous_id: anonymousId,
        utm_source: utmSource,
        landing_path: landingPath,
    }

    ATTRIBUTION_PARAMS.forEach((param) => {
        // utm_source is a required field and is already set when the payload is initialized above;
        // this loop only adds the optional attribution params.
        if (param === 'utm_source') {
            return
        }
        const value = searchParams.get(param)
        if (value) {
            payload[param] = value
        }
    })

    if (fbc) {
        payload.fbc = fbc
    }
    if (fbp) {
        payload.fbp = fbp
    }
    if (inAppBrowser) {
        payload.in_app_browser = inAppBrowser
    }

    return payload
}

/**
 * Reads the Segment anonymous id, invoking `onResolved` when one is available.
 *
 * Tries the live analytics client first, then the persisted anonymous id
 * (`resolveAnonymousTrackingId`), which itself falls back to the server-minted
 * `nile_ld_key` cookie present on virtually every request. When neither is
 * available yet — Segment hasn't finished initializing and no id is persisted —
 * it registers a one-shot `analytics.ready` callback so a late-minted id still
 * fires the touch. The touch is dropped only when no id ever resolves.
 */
export function resolveSegmentAnonymousId(onResolved: (anonymousId: string) => void): void {
    if (typeof window === 'undefined') {
        return
    }

    const resolve = (): string | undefined =>
        window.analytics?.user?.()?.anonymousId?.() || resolveAnonymousTrackingId()

    const resolved = resolve()
    if (resolved) {
        onResolved(resolved)
        return
    }

    // The id may only exist once Segment finishes initializing; retry then.
    window.analytics?.ready?.(() => {
        const late = resolve()
        if (late) {
            onResolved(late)
        }
    })
}

/**
 * Fires the CampaignTouch POST. Best-effort and non-blocking: the request runs
 * with `keepalive` so it survives an immediate navigation, and any network
 * error is swallowed so a failed touch has zero user-visible effect.
 *
 * Resolves to `true` only when the server acknowledges the touch with a
 * successful response, and `false` on any transport error or non-2xx status
 * (e.g. a 502 from the proxy). Callers use this to decide whether the touch was
 * actually recorded before persisting a dedup guard against it.
 */
export function postCampaignTouch(payload: CampaignTouchPayload): Promise<boolean> {
    if (typeof fetch === 'undefined') {
        return Promise.resolve(false)
    }

    return fetch(appPath(apiEndpoints.campaignTouches.create), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify(payload),
    })
        .then((response) => response.ok)
        .catch(() => false)
}
